import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/form-controls";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import {
  MOCK_SSO_CONFIG,
  SSO_TEST_RESULT,
  MOCK_MFA_POLICY,
  MOCK_SESSION_POLICY,
  MOCK_AUTH_CONFIG_AUDIT,
} from "@/data/mock";

/* AUTH-CFG-00/01/02/03 — Auth Configuration.
   Canonical source: prototype/pages/admin/auth-config.html (no current
   Figma frame located for any AUTH-CFG screen — documented, not assumed).
   The blueprint's own field tables describe 4 distinct screen IDs, but the
   prototype itself presents all 4 as stacked sections on ONE page with a
   single "Save Changes" button — it never separates them with tab UI, and
   AUTH-CFG-00 "Login Method Config" (provider/protocol/issuer) and
   AUTH-CFG-01 "IdP Setup" (client id/secret/metadata/test) are in fact the
   SAME "SSO Configuration" card in the source, not two separable sections.
   Per this task's own "do not assume all five screens require five
   separate routes" instruction and DD-004's own preferred interpretation
   (query-param tabs on one route, matching the already-established
   `/admin/deployment/[id]?step=X` convention), this is implemented as ONE
   route (`/admin/auth-config`) with 3 tabs — "Login & SSO" (AUTH-CFG-00 +
   AUTH-CFG-01 combined, matching the source's own single section),
   "MFA Policy" (AUTH-CFG-02), "Session & Device Policy" (AUTH-CFG-03) —
   plus the "Configuration Audit" section rendered persistently below the
   tabs (matching the source's own un-tabbed placement, since it is a
   cross-cutting log not tied to any single screen ID).

   DD-008 (Admin-Portal IdP config vs Sandbox Shell SSO runtime boundary)
   is resolved here the same way every other "settings"-style screen in
   this engagement has been resolved: this is a presentational
   configuration view over static fixture data, not wired to the real,
   frozen `useAuthStore` runtime login flow. Changing a value here does
   not alter `/login`'s actual behavior — the boundary DD-008 asks about
   is that there is no live data contract; this screen and the runtime
   login flow are deliberately not wired together, since building one
   would mean inventing a backend contract that does not exist. Every
   "Save"/"Test" action is local-state-only and says so. */

type Tab = "login" | "mfa" | "session";

const TAB_LABELS: Record<Tab, string> = {
  login: "Login & SSO",
  mfa: "MFA Policy",
  session: "Session & Device Policy",
};

const MFA_STATUS_TONE: Record<string, "danger" | "success" | "neutral"> = {
  Required: "danger",
  Allowed: "success",
  Disabled: "neutral",
};

const AUDIT_TAG_TONE: Record<string, "warning" | "info" | "danger"> = {
  MFA_POLICY: "warning",
  SSO_CONFIG: "info",
  MFA_DISABLED: "danger",
};

export function AuthConfig({ initialTab }: { initialTab: Tab }) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const canEdit = usePermission("auth:configure");

  // Login & SSO local state
  const [provider, setProvider] = useState(MOCK_SSO_CONFIG.provider);
  const [protocol, setProtocol] = useState(MOCK_SSO_CONFIG.protocol);
  const [issuerUrl, setIssuerUrl] = useState(MOCK_SSO_CONFIG.issuerUrl);
  const [clientIdRevealed, setClientIdRevealed] = useState(false);
  const [testResultVisible, setTestResultVisible] = useState(true);
  const [testing, setTesting] = useState(false);

  // MFA Policy local state
  const [mfaEnabled, setMfaEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(MOCK_MFA_POLICY.map((m) => [m.method, m.enabled]))
  );

  // Session Policy local state
  const [idleTimeout, setIdleTimeout] = useState(MOCK_SESSION_POLICY.idleTimeout);
  const [absoluteTimeout, setAbsoluteTimeout] = useState(MOCK_SESSION_POLICY.absoluteTimeout);
  const [maxSessions, setMaxSessions] = useState(MOCK_SESSION_POLICY.maxConcurrentSessions);

  function runSsoTest() {
    setTesting(true);
    setTestResultVisible(false);
    setTimeout(() => {
      setTestResultVisible(true);
      setTesting(false);
    }, 900);
  }

  function saveChanges() {
    toast({
      variant: "success",
      title: "Auth configuration saved",
      description: "Client-side fixture only — this configuration is not persisted to a backend and does not affect the live login flow.",
    });
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Auth Configuration</h1>
          <p className="mt-1 text-sm text-muted">Configure single sign-on, MFA policy, and session limits for your organization.</p>
        </div>
        <Link to="/admin/auth-diagnostics" className="text-sm font-semibold text-primary hover:underline">
          Open Diagnostics →
        </Link>
      </div>

      <div className="mb-5 flex border-b border-line">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === "login" && (
        <div className="mb-6 rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-base font-bold text-ink">SSO Configuration</h2>
            <Badge variant={MOCK_SSO_CONFIG.status === "Active" ? "success" : "neutral"} size="sm">{MOCK_SSO_CONFIG.status}</Badge>
          </div>
          <p className="mb-5 text-sm text-muted">Configure your identity provider for single sign-on access.</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="sso-provider">Provider</label>
              <Select id="sso-provider" value={provider} onChange={(e) => canEdit && setProvider(e.target.value)} disabled={!canEdit} className="w-full">
                {MOCK_SSO_CONFIG.providerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="sso-protocol">Protocol</label>
              <Select id="sso-protocol" value={protocol} onChange={(e) => canEdit && setProtocol(e.target.value)} disabled={!canEdit} className="w-full">
                {MOCK_SSO_CONFIG.protocolOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="sso-issuer">Issuer URL</label>
            <input
              id="sso-issuer"
              type="url"
              value={issuerUrl}
              readOnly={!canEdit}
              onChange={(e) => canEdit && setIssuerUrl(e.target.value)}
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary read-only:cursor-default read-only:bg-bg read-only:text-muted"
            />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink">Client ID</label>
              <input
                type="text"
                readOnly
                value={clientIdRevealed ? MOCK_SSO_CONFIG.clientIdRevealed : MOCK_SSO_CONFIG.clientIdMasked}
                className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 font-mono text-sm text-ink outline-none"
              />
              {!clientIdRevealed && (
                <button type="button" className="mt-1 text-xs font-semibold text-primary hover:underline" onClick={() => setClientIdRevealed(true)}>
                  Reveal
                </button>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink">Client Secret</label>
              <input
                type="text"
                readOnly
                value={MOCK_SSO_CONFIG.clientSecretMasked}
                className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 font-mono text-sm text-ink outline-none"
              />
              <div className="mt-1 text-xs text-muted">Stored encrypted in VaultCore</div>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="sso-metadata">Metadata URL</label>
            <input
              id="sso-metadata"
              type="url"
              defaultValue={MOCK_SSO_CONFIG.metadataUrl}
              readOnly={!canEdit}
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary read-only:cursor-default read-only:bg-bg read-only:text-muted"
            />
          </div>

          <Button variant="secondary" className="mt-4" disabled={testing || !canEdit} onClick={runSsoTest}>
            {testing ? "Testing…" : "Test SSO Connection"}
          </Button>
          {testResultVisible && (
            <div className="mt-3 flex items-start gap-2 rounded-[6px] border border-success-border bg-success-soft p-3">
              <span className="text-success">✓</span>
              <div>
                <div className="text-sm font-semibold text-success">{SSO_TEST_RESULT.title}</div>
                <div className="text-xs text-success opacity-80">{SSO_TEST_RESULT.detail}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "mfa" && (
        <div className="mb-6 rounded-[10px] border border-line bg-surface p-6">
          <h2 className="mb-1 text-base font-bold text-ink">MFA Policy</h2>
          <p className="mb-5 text-sm text-muted">Control which authentication factors are available and required.</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Enabled</th>
                <th className="px-3 py-2">Enforcement</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_MFA_POLICY.map((m) => (
                <tr key={m.method} className="border-b border-line last:border-b-0">
                  <td className="px-3 py-3">
                    <div className="font-semibold text-ink">{m.method}</div>
                    <div className="text-xs text-muted">{m.sub}</div>
                  </td>
                  <td className="px-3 py-3"><Badge variant={MFA_STATUS_TONE[m.status]} size="sm">{m.status}</Badge></td>
                  <td className="px-3 py-3">
                    <Toggle
                      checked={mfaEnabled[m.method]}
                      onCheckedChange={(v) => canEdit && setMfaEnabled((s) => ({ ...s, [m.method]: v }))}
                      ariaLabel={`Enable ${m.method}`}
                      disabled={!canEdit}
                    />
                  </td>
                  <td className="px-3 py-3 text-xs text-muted">{m.enforcement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "session" && (
        <div className="mb-6 rounded-[10px] border border-line bg-surface p-6">
          <h2 className="mb-1 text-base font-bold text-ink">Session Policy</h2>
          <p className="mb-5 text-sm text-muted">Control session lifetime and concurrent access.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="idle-timeout">Idle Timeout</label>
              <Select id="idle-timeout" value={idleTimeout} onChange={(e) => canEdit && setIdleTimeout(e.target.value)} disabled={!canEdit} className="w-full">
                {MOCK_SESSION_POLICY.idleTimeoutOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="abs-timeout">Absolute Timeout</label>
              <Select id="abs-timeout" value={absoluteTimeout} onChange={(e) => canEdit && setAbsoluteTimeout(e.target.value)} disabled={!canEdit} className="w-full">
                {MOCK_SESSION_POLICY.absoluteTimeoutOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="max-sessions">Max Concurrent Sessions</label>
              <Select id="max-sessions" value={maxSessions} onChange={(e) => canEdit && setMaxSessions(e.target.value)} disabled={!canEdit} className="w-full">
                {MOCK_SESSION_POLICY.maxConcurrentSessionsOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-[10px] border border-line bg-surface p-6">
        <h2 className="mb-1 text-base font-bold text-ink">Configuration Audit</h2>
        <p className="mb-4 text-sm text-muted">
          Recent changes to authentication configuration.{" "}
          <Link to="/evidence" className="text-primary hover:underline">View full evidence log →</Link>
        </p>
        <ul className="divide-y divide-line">
          {MOCK_AUTH_CONFIG_AUDIT.map((a, i) => (
            <li key={i} className="flex items-center gap-3 py-3">
              <span className="font-mono text-xs text-muted" style={{ minWidth: 140 }}>{a.timestamp}</span>
              <Avatar initials={a.actorInitials} size={24} fontSize={10} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink">{a.actor}</div>
                <div className="text-sm text-muted">{a.description}</div>
              </div>
              <Badge variant={AUDIT_TAG_TONE[a.tag]} size="sm" dot={false}>{a.tag}</Badge>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-3">
        {canEdit && <Button onClick={saveChanges}>Save Changes</Button>}
        <Link to="/admin/users" className="inline-flex h-9 items-center rounded-sm border border-line-strong bg-surface px-3.5 text-sm font-semibold text-ink transition-colors hover:bg-bg">
          Back to Admin
        </Link>
      </div>
    </div>
  );
}
